import {AuthVerifyData, fetchAuthVerify} from '@blesk-frontend/api';
import {bleskAuthRedirectStore, bleskAuthStore} from '@blesk-frontend/auth';
import {
  Button,
  FieldErrorMessage,
  InputOTP,
  InputOTPSeparator,
  InputOTPSlot,
} from '@blesk-frontend/ui';
import {Link} from '@tanstack/react-router';
import {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {toast} from 'sonner';

import {AuthPage} from '@/features/auth';
import {useAppPlatform} from '@/hooks';
import {updateExtensionToken} from '@/libs';
import {sendEvent} from '@/libs/analytics';
import {Route} from '@/routes/_auth/auth/verify';

export const AuthVerifyPage = () => {
  const {updateToken} = bleskAuthStore();
  const {redirectUrl} = bleskAuthRedirectStore();
  const navigate = Route.useNavigate();
  const {email, code, staffId} = Route.useSearch();
  const {setWindowsStatus} = useAppPlatform();

  const {
    formState: {errors, isSubmitting, isValid},
    control,
    handleSubmit,
  } = useForm<AuthVerifyData>({
    defaultValues: {
      email: email || '',
      code: code ? code.toString() : '',
      staffId: staffId,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const {accessToken, isSignUp} = await fetchAuthVerify(data);
      updateToken(accessToken);
      setWindowsStatus('loading');

      if (isSignUp) {
        sendEvent('auth/complete');
      }

      const inviteCode = sessionStorage.getItem('inviteCode');

      if (inviteCode) {
        await navigate({
          to: '/auth/invite',
          search: {code: inviteCode, accessToken: undefined, staffId: undefined},
        });
      } else if (redirectUrl === 'extension' || redirectUrl?.includes('/guides/extension')) {
        await updateExtensionToken(accessToken);
        window.close();
      } else if (redirectUrl === 'app') {
        await navigate({to: '/auth/app'});
      } else {
        await navigate({to: '/'});
        updateExtensionToken(accessToken).catch(() => {});
      }
    } catch (_err) {
      toast.error('Введен неверный код');
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (code) {
      onSubmit();
    }
  }, [code]);

  const onCompleteOtp = () => {
    onSubmit();
  };

  return (
    <AuthPage>
      <h1 className="text-center text-title-h1">Проверьте вашу почту</h1>

      <div className="text-center text-paragraph-sm text-text-secondary">
        Мы выслали письмо на {email}.<br /> Введите код из письма для продолжения.
      </div>

      <form className="flex flex-col gap-6" onSubmit={onSubmit}>
        <div>
          <div className="flex justify-center">
            <Controller
              control={control}
              name="code"
              rules={{
                required: 'Обязательное поле',
                minLength: {value: 6, message: 'Минимальная длина 6 символов'},
                maxLength: {value: 6, message: 'Максимальная длина 6 символов'},
              }}
              render={({field}) => (
                <InputOTP
                  {...field}
                  autoComplete="off"
                  autoFocus={true}
                  maxLength={6}
                  onComplete={onCompleteOtp}
                >
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSeparator />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTP>
              )}
            />
          </div>
          <div className="text-center">
            <FieldErrorMessage errors={errors} name="code" />
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={!isValid} loading={isSubmitting}>
          Далее
        </Button>

        <div className="text-center text-paragraph-sm">
          <Link to="/auth/signin">Вернуться назад</Link>
        </div>
      </form>
    </AuthPage>
  );
};
